import { getContactPerson } from '../customers/index.js';
import { Entity } from '../index.js';
import { assertUnreachable } from '../util/index.js';
import { EBoksRecipientType } from './eboks.js';
export var IncomingState;
(function (IncomingState) {
    IncomingState["UNMAPPED"] = "UNMAPPED";
    IncomingState["MAPPED"] = "MAPPED";
    IncomingState["HANDLED"] = "HANDLED";
    IncomingState["DELETED"] = "DELETED";
})(IncomingState || (IncomingState = {}));
export var OutgoingState;
(function (OutgoingState) {
    OutgoingState["PENDING"] = "PENDING";
    OutgoingState["QUEUED"] = "QUEUED";
    OutgoingState["SENT"] = "SENT";
    OutgoingState["CANCELLED"] = "CANCELLED";
})(OutgoingState || (OutgoingState = {}));
export var InteractionType;
(function (InteractionType) {
    InteractionType["Eboks"] = "EBOKS";
    InteractionType["Kivra"] = "KIVRA";
    InteractionType["Digipost"] = "DIGIPOST";
    InteractionType["Email"] = "EMAIL";
    InteractionType["SMS"] = "SMS";
    InteractionType["Letter"] = "LETTER";
    InteractionType["Phone"] = "PHONE";
    InteractionType["Note"] = "NOTE";
    InteractionType["Personal"] = "PERSONAL";
    InteractionType["Other"] = "OTHER";
    InteractionType["ClientNote"] = "CLIENT NOTE";
    InteractionType["DebtorNote"] = "DEBTOR NOTE";
    InteractionType["AutoCall"] = "AUTO CALL";
    InteractionType["EInvoice"] = "EINVOICE";
})(InteractionType || (InteractionType = {}));
export var Direction;
(function (Direction) {
    Direction["OUTGOING"] = "OUTGOING";
    Direction["INCOMING"] = "INCOMING";
})(Direction || (Direction = {}));
export var ReturnReason;
(function (ReturnReason) {
    ReturnReason["UNKNOWNRECIPIENT"] = "UNKNOWNRECIPIENT";
    ReturnReason["NOTDELIVERABLE"] = "NOTDELIVERABLE";
    ReturnReason["DENIED"] = "DENIED";
})(ReturnReason || (ReturnReason = {}));
export var PhoneOutgoingTag;
(function (PhoneOutgoingTag) {
    PhoneOutgoingTag["NoAnswer"] = "NO_ANSWER";
    PhoneOutgoingTag["PaymentPlan"] = "PAYMENT_PLAN";
    PhoneOutgoingTag["OtherAgreement"] = "OTHER_AGREEMENT";
    PhoneOutgoingTag["IncorrectNumber"] = "INCORRECT_NUMBER";
    PhoneOutgoingTag["WrongNumber"] = "WRONG_NUMBER";
    PhoneOutgoingTag["FullPayment"] = "FULL_PAYMENT";
    PhoneOutgoingTag["Opposition"] = "OPPOSITION";
    PhoneOutgoingTag["Other"] = "OTHER";
})(PhoneOutgoingTag || (PhoneOutgoingTag = {}));
export var PhoneIncomingTag;
(function (PhoneIncomingTag) {
    PhoneIncomingTag["PaymentPlan"] = "PAYMENT_PLAN";
    PhoneIncomingTag["OtherAgreement"] = "OTHER_AGREEMENT";
    PhoneIncomingTag["FullPayment"] = "FULL_PAYMENT";
    PhoneIncomingTag["Opposition"] = "OPPOSITION";
    PhoneIncomingTag["Other"] = "OTHER";
    PhoneIncomingTag["Billing"] = "BILLING";
    PhoneIncomingTag["Terms"] = "TERMS";
    PhoneIncomingTag["Caseworking"] = "CASEWORKING";
})(PhoneIncomingTag || (PhoneIncomingTag = {}));
export const EntityPhoneTags = {
    [Entity.Case]: [
        PhoneOutgoingTag.NoAnswer,
        PhoneOutgoingTag.PaymentPlan,
        PhoneOutgoingTag.OtherAgreement,
        PhoneOutgoingTag.IncorrectNumber,
        PhoneOutgoingTag.WrongNumber,
        PhoneOutgoingTag.FullPayment,
        PhoneOutgoingTag.Opposition,
        PhoneOutgoingTag.Other,
        PhoneIncomingTag.PaymentPlan,
        PhoneIncomingTag.OtherAgreement,
        PhoneIncomingTag.FullPayment,
        PhoneIncomingTag.Opposition,
        PhoneIncomingTag.Other,
    ],
    [Entity.Creditor]: [
        PhoneIncomingTag.Other,
        PhoneIncomingTag.Billing,
        PhoneIncomingTag.Terms,
        PhoneIncomingTag.Caseworking,
    ],
};
export var InteractionProvider;
(function (InteractionProvider) {
    InteractionProvider["System"] = "SYSTEM";
    InteractionProvider["SparkPost"] = "SPARK_POST";
    InteractionProvider["Mailgun"] = "MAILGUN";
    InteractionProvider["LinkMobility"] = "LINK_MOBILITY";
    InteractionProvider["Sproom"] = "SPROOM";
    InteractionProvider["VonageSMS"] = "VONAGE_SMS";
    InteractionProvider["VonageCall"] = "VONAGE_CALL";
    InteractionProvider["Straalfors"] = "STRAALFORS";
    InteractionProvider["EBoks"] = "E_BOKS";
})(InteractionProvider || (InteractionProvider = {}));
export const autoCallLookupFormat = (from, to) => `from:${from},to:${to}`;
// Prefer the DAR address id; a KVHX can only be derived from one, so the
// fallback is a KVHX stored on the address before DAWA closed or imported
const splitName = (name) => {
    const names = name.split(' ');
    return {
        firstName: names.slice(0, -1).join(' '),
        lastName: names[names.length - 1],
    };
};
export const nameAndAddressRecipient = (name, addressId, kvhx) => {
    const { firstName, lastName } = splitName(name);
    if (addressId !== null) {
        return {
            type: EBoksRecipientType.AddressId,
            firstName,
            lastName,
            addressId,
        };
    }
    if (kvhx != null && kvhx !== '') {
        return { type: EBoksRecipientType.KVHX, firstName, lastName, kvhx };
    }
    return null;
};
// Whether an earlier name and address delivery was to this person at this
// address, whichever identifier it was sent with. An address without the
// attempt's identifier is another address: sending stores the address id it
// resolved, and a replaced address loses the old KVHX
export const isNameAndAddressAttemptAt = (recipient, name, address) => {
    if (recipient.type === EBoksRecipientType.CentralRegisterId) {
        return false;
    }
    const { firstName, lastName } = splitName(name);
    if (recipient.firstName !== firstName || recipient.lastName !== lastName) {
        return false;
    }
    return recipient.type === EBoksRecipientType.AddressId
        ? recipient.addressId === address.DK_AdresseId
        : recipient.kvhx === address.KVHX;
};
export const genRecipientsRaw = (customer, _case, options) => {
    const recipients = [];
    const contactPerson = getContactPerson(customer, _case);
    const centralRegisterId = customer.isCompany
        ? contactPerson?.details.cvr
        : contactPerson?.details.cpr;
    if (centralRegisterId != null && centralRegisterId != '') {
        recipients.push({
            type: EBoksRecipientType.CentralRegisterId,
            centralRegisterId,
            isCompany: customer.isCompany,
        });
    }
    const name = !customer.isCompany ? contactPerson?.details.name : null;
    if (name != null) {
        const recipient = nameAndAddressRecipient(name, options.addressId, contactPerson?.details.address?.KVHX);
        if (recipient !== null) {
            recipients.push(recipient);
        }
    }
    return recipients;
};
export const findRecipient = (recipients, customer, _case) => {
    const contactPerson = getContactPerson(customer, _case);
    if (contactPerson?.details.deliverability?.eboks &&
        contactPerson.details.deliverability.eboks.length > 0) {
        const priorAttempts = contactPerson.details.deliverability.eboks
            .map((x) => x)
            .reverse();
        for (const recipient of recipients) {
            const match = priorAttempts.find((x) => isSameRecipient(x.recipient, recipient));
            if (match != null) {
                if (!match.success) {
                    continue;
                }
            }
            return recipient;
        }
        return null;
    }
    return recipients[0] ?? null;
};
export const isSameRecipient = (a, b) => {
    switch (a.type) {
        case EBoksRecipientType.KVHX:
            return (b.type === EBoksRecipientType.KVHX &&
                a.firstName === b.firstName &&
                a.lastName === b.lastName &&
                a.kvhx === b.kvhx);
        case EBoksRecipientType.AddressId:
            return (b.type === EBoksRecipientType.AddressId &&
                a.firstName === b.firstName &&
                a.lastName === b.lastName &&
                a.addressId === b.addressId);
        case EBoksRecipientType.CentralRegisterId:
            return (b.type === EBoksRecipientType.CentralRegisterId &&
                a.isCompany === b.isCompany &&
                a.centralRegisterId === b.centralRegisterId);
    }
    assertUnreachable(a);
};
/**
 * Checks if an email interaction is part of a thread.
 * An email is part of a thread if it has inReplyTo or references set.
 */
export const isEmailPartOfThread = (details) => {
    return Boolean(details.inReplyTo || details.references);
};
//# sourceMappingURL=index.js.map